import type { Row } from "./api";

const supplementalDraftIds = new Set([
  "INT.taxpayer.name",
  "INT.taxpayer.mailing_address",
  "INT.taxpayer.contact",
  "INT.taxpayer.ip_pin",
  "INT.taxpayer.occupation",
  "INT.refund.intent",
  "INT.return.designations",
  "INT.return.preparer_profile",
  "INT.inventory.expected_statement",
]);
export function canCapture(field: Row, kind: string, role: string): boolean {
  if (!["workspace_admin", "preparer"].includes(role)) return false;
  if (kind === "questionnaire") return field.stage === "PRECALC";
  if (kind === "supplemental-facts") return supplementalDraftIds.has(field.id);
  return (
    field.owner === "PREPARER_CAPTURE" &&
    Array.isArray(field.stages) &&
    field.stages.length > 0 &&
    field.stages.every((s: string) => s === "PRECALC")
  );
}
export function structuredCapture(field: Row): boolean {
  return (
    ["object", "array"].includes(field.valueSchema?.type) ||
    /object|array|set|repeat|\$ref|#\//i.test(String(field.type))
  );
}
export function serializeDraftValue(value: unknown): string {
  return value === undefined
    ? ""
    : typeof value === "object"
      ? JSON.stringify(value, null, 2)
      : String(value);
}
export function parseDraftValue(field: Row, text: string): unknown {
  if (text === "")
    throw new Error(
      "Enter an explicit value. Empty does not mean zero, false, or not applicable.",
    );
  if (structuredCapture(field)) {
    try {
      return JSON.parse(text);
    } catch {
      throw new Error(
        "Enter valid structured JSON matching the source contract.",
      );
    }
  }
  if (field.options?.length) {
    const match = field.options.find(
      (option: unknown) =>
        String(
          typeof option === "object" && option !== null
            ? ((option as Row).value ??
                (option as Row).id ??
                (option as Row).code)
            : option,
        ) === text,
    );
    if (match === undefined)
      throw new Error("Choose one of the source-defined values.");
    return typeof match === "object" && match !== null
      ? (match.value ?? match.id ?? match.code)
      : match;
  }
  const type = String(field.valueSchema?.type || field.type);
  if (/boolean/.test(type)) {
    if (!["true", "false"].includes(text))
      throw new Error("Choose an explicit yes or no.");
    return text === "true";
  }
  if (/integer/.test(type)) {
    if (!/^-?\d+$/.test(text) || !Number.isSafeInteger(Number(text)))
      throw new Error("Enter an exact, safe whole number.");
    return Number(text);
  }
  return text;
}
